import type { CharacterEquipmentLoadout, EssencePool, RecipeDefinition } from '@sorrem/shared';
import type { UICoalescence, UIInventoryItem } from '@sorrem-client/types';
import { resolveComparedEquipment, toEquipmentSlots } from '@sorrem-client/ui/shared/equipment-slots';
import { CoalesceSection } from './components/CoalesceSection';
import { CrystalStackHeader } from './components/CrystalStackHeader';
import { ItemCard } from './components/ItemCard';

interface CarriedSlotContextProps {
    slotIndex: number;
    /** What occupies the slot. */
    item: UIInventoryItem | undefined;
    /** Coalescences headed for the slot, soonest first. */
    pending: readonly UICoalescence[];
    /** The essence coalescing can spend. */
    essencePool: EssencePool;
    /** Essence value the run's commission holds back from coalescing; noted when above zero. */
    heldEssenceValue?: number;
    /** Crystals form at once (on the gathering page) rather than over time. */
    isInstant?: boolean;
    onStartCoalesce: (recipe: RecipeDefinition) => Promise<string | null>;
    onCancelCoalesce: (coalescenceId: number) => void;
    /** A crystal stack's action: Use in a run, Remove on the gathering page. */
    crystalAction: { label: string; onClick: (item: UIInventoryItem) => void };
    /** What's equipped, so an item card can compare against what it would replace. */
    equipped: CharacterEquipmentLoadout;
    /** Equips a carried piece of equipment, swapping out what it replaces. */
    onEquip: (item: UIInventoryItem) => void;
    /** Drops the slot's whole item on the ground, in a run; no Drop action when unset. */
    onDrop?: (item: UIInventoryItem) => void;
}

/**
 * What the Carried panel shows for its selected slot: Coalesce for an empty
 * slot, the stack and Coalesce for a crystal stack, or an item card for
 * equipment. The same in a run and on the gathering page.
 */
export function CarriedSlotContext({
    slotIndex,
    item,
    pending,
    essencePool,
    heldEssenceValue = 0,
    isInstant = false,
    onStartCoalesce,
    onCancelCoalesce,
    crystalAction,
    equipped,
    onEquip,
    onDrop,
}: CarriedSlotContextProps): JSX.Element {
    if (item === undefined || item.crystal !== undefined) {
        return (
            <>
                {item !== undefined && (
                    <CrystalStackHeader
                        item={item}
                        actionLabel={crystalAction.label}
                        onAction={() => crystalAction.onClick(item)}
                        onDrop={onDrop === undefined ? undefined : (): void => onDrop(item)}
                    />
                )}
                <CoalesceSection
                    key={slotIndex}
                    slotIndex={slotIndex}
                    item={item}
                    pending={pending}
                    essencePool={essencePool}
                    heldEssenceValue={heldEssenceValue}
                    isInstant={isInstant}
                    onStart={onStartCoalesce}
                    onCancel={onCancelCoalesce}
                />
            </>
        );
    }

    return (
        <ItemCard
            item={item}
            compared={resolveComparedEquipment(item, toEquipmentSlots(equipped))}
            onEquip={item.equipmentType === undefined ? undefined : (): void => onEquip(item)}
            onDrop={onDrop === undefined ? undefined : (): void => onDrop(item)}
        />
    );
}
